<template>
  <main-layout
    controlsBg="light"
    customControls
    :breadcrumb="getBreadcrumb"
    :showPoweredBy="false"
    :has-settings="true">
    <explorer-topbar-settings
      slot="settings"
      v-on:onClickDownload="onClickDownload"
      v-on:onClickCopy="showCopyModal"
      v-on:onClickSave="showSaveModal"/>
    <div slot="custom-controls-top">
      <div class="controls__filters note">
        <controls-filter
          dateId="startDate"
          :filterTitle="$t('explorer.from')"
          formatDate="DD MMMM YYYY / HH:mm:ss"
          :disabledDate="disabledDate"
          :startDate="getStartDateString">
        </controls-filter>
        <controls-filter
          dateId="endDate"
          :filterTitle="$t('explorer.to')"
          formatDate="DD MMMM YYYY / HH:mm:ss"
          :disabledDate="disabledDate"
          :startDate="getEndDateString">
        </controls-filter>
      </div>
      <btn-pill
        class="update-button"
        :btnText="$t('explorer.menu.explore')"
        btn-size="xs"
        v-on:click="onClickExplore()">
      </btn-pill>
    </div>
    <div class="container">
      <div class="header">
        <div class="header__title">
          <h1
            class="flex">
            <chevron
              @click="back()"/>
            {{$t('explorer.multipleCorrelation')}}
            <div
              class="flex"
              v-if="getSampledVariables().length > 0"
              v-tooltip.right.top-start="{ content: '<b>' + $t('explorer.sampledVariables') + '</b></br>' + getSampledVariablesTooltipContent }">
              <font-awesome-icon
                class="icon"
                :icon="icon.exclamation"/>
            </div>
          </h1>
        </div>
        <tags-selector
          v-model="pairsSelected"
          :options="pairsOptions"
          :max-values="maxNumberVariablePairs()">
        </tags-selector>
        <options-correlation
          class="options-correlation"
          ref="optionsCorrelation"
          v-on:onGridStateChange="onGridStateChange"
          v-on:onExchangeAxes="onExchangeAxes"
          v-on:onEvolution="onEvolution"
          v-on:onFixedAxes="onFixedAxes"/>
      </div>
      <div
        id="scatter-container"
        class="scatter-container">
        <chart-multiple-correlation
          :pairs="variablePairs"
          :data="getCorrelationData"
          :range="range"
          :grid="grid"
          :width="chartSize.width"
          :height="chartSize.height" />
      </div>
    </div>
    <fixed-range-modal
      ref="fixedRangeModal"
      :data="getCorrelationData"
      v-on:change="onFixedAxesRangeChanged"/>
    <copy-url-modal
      ref="copyUrlModal"/>
    <query-save-modal
      ref="querySaveModal"
      v-on:save="createExplorerQuery"/>
  </main-layout>
</template>

<script>
import {eventBus} from '@/main';
import config from 'BRAND/config';
import {mapActions, mapGetters} from 'vuex';
import MainLayout from '@/layouts/MainLayout';
import ControlsFilter from '@/components/shared/controlsbar/ControlsFilter';
import dayjs from 'dayjs';
import BtnPill from '@/components/shared/buttons/BtnPill';
import AcceptModal from '@/components/shared/dialogs/AcceptModal';
import Checkbox from '@/components/shared/forms/Checkbox';
import InlineTip from '@/components/explorer/InlineTip';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {faExclamationCircle} from '@fortawesome/pro-regular-svg-icons';
import CopyUrlModal from '@/components/explorer/CopyUrlModal';
import QuerySaveModal from '../../components/explorer/QuerySaveModal';
import explorerToCSV from '@/layouts/explorer/explorerToCSV';
import Chevron from '@/components/shared/Chevron';
import ExplorerTopbarSettings from '@/components/explorer/ExplorerTopbarSettings';
import ChartMultipleCorrelation from '@/components/explorer/correlation/ChartMultipleCorrelation';
import TagsSelector from '@/components/explorer/multiple/TagsSelector';
import OptionsCorrelation from '@/components/explorer/correlation/OptionsCorrelation';
import FixedRangeModal from '@/components/explorer/correlation/FixedRangeModal';
import navigation from '@/layouts/explorer/navigation';
import variableApi from '@/api/variable';

export default {
  name: 'ExplorerMultipleCorrelation',
  components: {
    TagsSelector,
    ChartMultipleCorrelation,
    ExplorerTopbarSettings,
    MainLayout,
    ControlsFilter,
    BtnPill,
    AcceptModal,
    Checkbox,
    InlineTip,
    FontAwesomeIcon,
    CopyUrlModal,
    QuerySaveModal,
    Chevron,
    OptionsCorrelation,
    FixedRangeModal
  },
  data () {
    return {
      pairsSelected: [],
      pairsOptions: [],
      filterTagsX: [],
      filterTagsY: [],
      beginDate: getInitialStartDate(),
      endDate: getInitialEndDate(),
      variablePairs: [],
      icon: {
        exclamation: faExclamationCircle
      },
      chartSize: {
        width: 0,
        height: 0
      },
      grid: false,
      range: { x: [], y: [] },
      origin: null,
      filter: null
    };
  },
  watch: {
    pairs: {
      handler () {
        this.variablePairs = this.pairs;
      },
      deep: true
    }
  },
  created () {
    this.retrieveTagsTree({ context: this }).then(() => {
      const query = this.$route.query;
      if (query.beginDate && query.endDate) {
        this.beginDate = new Date(Number(query.beginDate));
        this.endDate = new Date(Number(query.endDate));
      }
      if (query.origin) {
        this.origin = query.origin;
      }
      if (query.filter) {
        this.filter = query.filter;
      }
      if (Object.keys(query).length > 0) {
        this.filterTagsX = query.filterTagsX.split(',').map(i => Number(i));
        this.filterTagsY = query.filterTagsY.split(',').map(i => Number(i));
        this.pairsSelected = query.pairsSelected.split(',').map(i => Number(i));
      }
      this.calculatePairsOptions();
    });

    eventBus.$on('dateSelected', this.onDateSelected);
  },
  mounted () {
    const scatterContainer = document.getElementById('scatter-container');
    if (scatterContainer !== null) {
      this.chartSize.width = scatterContainer.offsetWidth;
      this.chartSize.height = scatterContainer.offsetHeight;
    }
    this.$refs.fixedRangeModal.setRange(this.range);
    this.$refs.optionsCorrelation.setGridState(this.grid);
  },
  destroyed () {
    eventBus.$off('dateSelected', this.onDateSelected);
  },
  computed: {
    ...mapGetters([
      'getLocale',
      'getVariables',
      'getTagsTreeState',
      'getCorrelationData'
    ]),
    getStartDateString () {
      return this.beginDate.toString();
    },
    getEndDateString () {
      return this.endDate.toString();
    },
    getBreadcrumb () {
      if (this.filter) {
        return navigation.filteredBreadcrumb(this);
      } else {
        switch (this.origin) {
          case 'QueryList':
            return [
              {link: '/workspace', nameKey: 'menu.workspace'},
              {link: '/query/list', nameKey: 'query.title'},
              {nameKey: 'menu.correlation', disabled: true}
            ].map(route => {
              route.name = this.$t(route.nameKey);
              return route;
            });
          default:
            const relativePath = this.$route.fullPath.split('?')[1];
            return [
              {name: this.$t('explorer.breadcrumbMultipleCorrelation'), link: '/explore'},
              {
                name: this.$t('explorer.parameterSelection'),
                link: '/explorer-multiple-correlation-variables?' + relativePath
              }, // Dirty hack to emulate 'back' button
              {name: this.$t('menu.correlation'), disabled: true}
            ];
        }
      }
    },
    getSampledVariablesTooltipContent () {
      let variables = [];
      for (let i = 0; i < this.getSampledVariables().length; i++) {
        variables.push(this.getSampledVariables()[i].metadata.label);
      }
      return variables.join(',</br>');
    },
    pairs () {
      const pairs = [];

      for (let i = 0; i < this.pairsOptions.length; i++) {
        if (this.pairsSelected.includes(this.pairsOptions[i].id)) {
          pairs.push({
            label: this.pairsOptions[i].label,
            x: this.pairsOptions[i].x,
            y: this.pairsOptions[i].y
          });
        }
      }
      return pairs;
    }
  },
  methods: {
    ...mapActions([
      'retrieveVariables',
      'retrieveTagsTree',
      'retrieveCorrelationData',
      'createQuery'
    ]),
    back () {
      switch (this.origin) {
        case 'QueryList':
          const query = this.filter ? {filter: navigation.FILTER_EXPLORER_MULTIPLE_CORRELATION} : null;
          this.$router.push({name: 'QueryList', query: query});
          break;
        default:
          this.$router.push({name: 'ExplorerMultipleCorrelationVariables', query: this.$route.query});
      }
    },
    disabledDate (date) {
      return date > new Date();
    },
    onClickExplore () {
      this.updateUrl();
      this.requestData();
    },
    onDateSelected (event) {
      if (event.id === 'startDate') {
        this.beginDate = event.date;
      }
      if (event.id === 'endDate') {
        this.endDate = event.date;
      }
    },
    calculatePairsOptions () {
      if (this.filterTagsX.length > 0 && this.filterTagsY.length > 0) {
        variableApi.getMultipleCorrelationVariablePairs(this, this.filterTagsX.join(), this.filterTagsY.join()).then(data => {
          this.pairsOptions = data.body;

          this.requestData();
        });
      } else {
        this.pairsOptions = [];
      }
    },
    requestData () {
      const payload = {
        context: this,
        variableIds: this.listVariableIds().join(','),
        startDate: this.beginDate,
        endDate: this.endDate
      };
      this.retrieveCorrelationData(payload);
    },
    updateUrl () {
      const query = {
        filterTagsX: this.filterTagsX.join(),
        filterTagsY: this.filterTagsY.join(),
        pairsSelected: this.pairsSelected.join(),
        beginDate: this.beginDate.getTime(),
        endDate: this.endDate.getTime(),
        optGrid: String(this.grid)
      };

      if (this.origin) {
        query.origin = this.origin;
      }
      if (this.filter) {
        query.filter = this.filter;
      }

      this.$router.push({ query: query });
    },
    listVariableIds () {
      const variableIds = [];
      for (let i = 0; i < this.pairsOptions.length; i++) {
        if (this.pairsSelected.includes(this.pairsOptions[i].id)) {
          variableIds.push(this.pairsOptions[i].x);
          variableIds.push(this.pairsOptions[i].y);
        }
      }
      return variableIds;
    },
    createExplorerQuery (options) {
      const queryName = options.name;
      const queryWithDates = options.dates;

      let relativePath = this.$route.fullPath;
      relativePath = relativePath.replace('&filter=' + this.filter, '');
      relativePath = relativePath.replace('&origin=' + this.origin, '');
      if (!queryWithDates) {
        // Remove dates from path
        const queryPath = this.$route.query;
        relativePath = relativePath.replace('&startDate=' + queryPath.startDate, '');
        relativePath = relativePath.replace('?startDate=' + queryPath.startDate, '?');
        relativePath = relativePath.replace('&endDate=' + queryPath.endDate, '');
        relativePath = relativePath.replace('?endDate=' + queryPath.endDate, '?');
        relativePath = relativePath.replace('?&', '?');
      }

      const query = {
        name: queryName,
        relativePath: relativePath,
        star: false
      };
      this.createQuery({ context: this, query: query });
    },
    showCopyModal () {
      this.$refs.copyUrlModal.show();
    },
    showSaveModal () {
      this.$refs.querySaveModal.show();
    },
    onClickDownload () {
      explorerToCSV.downloadCorrelation(this.getCorrelationData.variables, this.getLocale);
    },
    getSampledVariables () {
      return this.getCorrelationData.variables.filter(variable => variable.sampled);
    },
    maxNumberVariablePairs () {
      return MAXIMUM_NUMBER_OF_ALLOWED_VARIABLE_PAIRS;
    },
    onGridStateChange (gridStatus) {
      this.grid = gridStatus;
      this.updateUrl();
    },
    onExchangeAxes () {
      for (let i = 0; i < this.variablePairs.length; i++) {
        let aux = this.variablePairs[i].x;
        this.variablePairs[i].x = this.variablePairs[i].y;
        this.variablePairs[i].y = aux;
      }
      this.requestData();
    },
    onEvolution () {
      const query = {
        variables: this.listVariableIds().join((',')),
        startDate: this.beginDate.getTime(),
        endDate: this.endDate.getTime()
      };
      this.$router.push({name: 'ExplorerEvolution', query: query});
    },
    onFixedAxes () {
      this.$refs.fixedRangeModal.show();
    },
    onFixedAxesRangeChanged (range) {
      this.range = range;
      this.updateUrl();
    }
  }
};

const MAXIMUM_NUMBER_OF_ALLOWED_VARIABLE_PAIRS = 4;

const getInitialStartDate = () => {
  const startOf = config.explorerDefaultDate.startOf;
  const gap = config.explorerDefaultDate.gapBegin;
  const unit = config.explorerDefaultDate.unit;
  return dayjs().startOf(startOf).subtract(gap, unit).toDate();
};

const getInitialEndDate = () => {
  const startOf = config.explorerDefaultDate.startOf;
  const gap = config.explorerDefaultDate.gapEnd;
  const unit = config.explorerDefaultDate.unit;
  return dayjs().startOf(startOf).subtract(gap, unit).toDate();
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .container {
    padding-top: $sp-margin;
    padding-bottom: $sp-margin;
    flex-grow: 1;
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  .scatter-container {
    display: flex;
    height: 100%;
    width: 100%;
    flex: 1;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: end;

    &__title {
      margin-bottom: $sp-margin;
      @media (min-width: $breakpoint) {
        display: flex;
        h1 {
          margin-bottom: 0;
          margin-right: $sp-margin;
          padding-right: $sp-margin;
        }
      }
    }
  }

  .chevron {
    font-size: $fs-2xl;
    color: $jamaicol;
    margin-left: auto;
    margin-right: $sp-margin-smaller;
    cursor: pointer;
  }

  .opacity:hover {
    opacity: 0.3;
    transition: opacity 0.3s;
  }

  .update-button {
    margin-left: $sp;
  }

  .flex {
    display: flex;
    align-items: center;
  }

  .icon {
    font-size: $fs;
    color: $jamaicol;
    margin-left: $sp-margin;
  }

  .options-correlation {
    margin-top: $sp-margin-small;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/explorer/ExplorerMultipleCorrelation.vue